<template>
    <v-container fluid px-1>
        <div class="map-template-container">
            <div id="app-additional-layers-button" class="level1-z-index">

                <v-btn
                        :input-value="layer.show"
                        active-class="active"
                        v-for="(layer) in layers"
                        :key="layer.id"
                        x-small
                        class="app-layer-button"
                        :title="$t(layer.title)"
                        @click="toggleAdditionalLayer(layer); handleAdditionalLayer(layer)"
                >
                    {{  $t(layer.name) }}
                </v-btn>

                <v-btn x-small class="app-layer-button" @click="toggleFullscreen">
                    <v-icon small v-if="!fullScreenActivated" :title="$t('title.enterFullscreen')">mdi-fullscreen</v-icon>
                    <v-icon small v-else :title="$t('title.exitFullscreen')">mdi-fullscreen-exit</v-icon>
                </v-btn>
            </div>
            <div v-if="!mapReady">
                <map-updating></map-updating>
            </div>

            <div id="map-container">
                <observation-popup
                        v-if="renderPopupObservation"
                        :show="showPopupObservation"
                        :headers="headers"
                        :observations="observations"

                        :X="mousePositionControl.getProjection().code_ === 'EPSG:21781' ? Math.ceil(Math.trunc(X)/5000)*5000 - 2500 : (Math.ceil(Math.trunc(X)/5000)*5000 - 2500)+2000000"
                        :Y="mousePositionControl.getProjection().code_ === 'EPSG:21781' ? Math.ceil(Math.trunc(Y)/5000)*5000 - 2500 : (Math.ceil(Math.trunc(Y)/5000)*5000 - 2500)+1000000"
                        @cancel="observations=[]; showPopupObservation=false"
                />
                <div id="mask-container" class="hide"></div>
            </div>


            <v-row no-gutters class="mb-1 mt-0">

                <v-col cols="6"
                       xl="6"
                       lg="6"
                       md="6"
                       sm="6"
                       xs="6">
                    <div id="app-cursor-position">

                        <v-row no-gutters>

                            <v-col cols="5"
                                   xl="4"
                                   lg="4"
                                   md="5"
                                   sm="5"
                                   xs="5">
                                <v-select
                                        :items="coordinateSystems"
                                        dense
                                        style="font-size: 12px"
                                        prepend-inner-icon="mdi-crosshairs-gps"
                                        :value="coordinateSystems[0]"
                                        :title="$t('title.coordinates')"
                                        @change="changeMousePositionProjection($event)"
                                />
                            </v-col>

                            <v-col cols="7"
                                   xl="8"
                                   lg="8"
                                   md="7"
                                   sm="7"
                                   xs="7">

                                <div id="app-coordinates-display"></div>

                            </v-col>
                        </v-row>
                    </div>
                </v-col>
                <v-col cols="6"
                       xl="6"
                       lg="6"
                       md="6"
                       sm="6"
                       xs="6">
                    <map-download :map="map"></map-download>
                </v-col>

            </v-row>

        </div>
    </v-container>
</template>
<script>
    import {diagramUrl, mapServiceUrls, speciesUrl} from '../../../data/app-data'
    import MapUpdating from './MapUpdating'
    import ObservationPopup from '../MapObservationPopup'
    import MapDownload from '../MapDownload'
    import 'ol/ol.css'
    import Map from 'ol/Map'
    import View from 'ol/View'
    import {Image as ImageLayer} from 'ol/layer'
    import {ImageArcGISRest, WMTS} from 'ol/source'
    import TileWMS from 'ol/source/TileWMS'
    import i18n from '../../../plugins/i18n'
    import {API_ERROR_MESSAGE, OBSERVATION_ERROR_MESSAGE} from '../../../data/constants'
    import axios from 'axios'
    import WMTSTileGrid from 'ol/tilegrid/WMTS'
    import TileLayer from 'ol/layer/Tile'
    import {get, transform} from 'ol/proj'
    import proj4 from 'proj4'
    import {register} from 'ol/proj/proj4'
    import MousePosition from 'ol/control/MousePosition'
    import {createStringXY} from 'ol/coordinate'
    import {defaults as defaultControls, ScaleLine} from 'ol/control'
    import {defaults} from 'ol/interaction'
    import MouseWheelZoom from 'ol/interaction/MouseWheelZoom'
    import DragPan from 'ol/interaction/DragPan'
    import {noModifierKeys} from 'ol/events/condition'

    export default {
        name: 'MapOpenLayers',
        components: {MapUpdating, ObservationPopup, MapDownload},
        data() {
            return {
                map: null,
                swissBaseReliefLayer: null,
                cantonLayer: null,
                bioRegLayer: null,
                mousePositionControl: null,
                projectionLV95: null,
                projectionLV03: null,
                dynamicLayerInsideTimeRange: null,
                dynamicLayerOutsideTimeRange: null,
                dynamicLayers: null,
                observationLayer: null,
                fullScreenActivated: false,
                observations: [],
                headers: null,
                X: 0,
                Y: 0,
                showPopupObservation: false,
                renderPopupObservation: false,
                observationData: null,
                topoMap: {
                    'attribution': 'swisstopo',
                    'format': 'jpeg',
                    'resolutions': [4000, 3750, 3500, 3250, 3000, 2750, 2500, 2250, 2000, 1750, 1500, 1250, 1000, 750, 650, 500, 250, 100, 50, 20, 10, 5, 2.5, 2, 1.5, 1, 0.5, 0.25],
                    'extent': [2420000, 965000, 2900000, 1350000]
                },
                coordinateSystems: ['LV03/CH1903', 'LV95/CH1903+'],
                layers: [
                    {
                        id: 'ch.bafu.biogeographische_regionen',
                        show: false,
                        opacity: 0.15,
                        name: 'map.layerBioReg',
                        title: 'title.showBioGeoReg'
                    },
                    {
                        id: 'ch.swisstopo.swissboundaries3d-kanton-flaeche.fill',
                        show: false,
                        opacity: 0.8,
                        name: 'map.layerCantons',
                        title: 'title.showCantons'
                    },
                    {
                        id: 'ch.bafu.landesforstinventar-vegetationshoehenmodell_relief',
                        show: true,
                        opacity: 1,
                        name: 'map.layerRelief',
                        title: 'title.showRelief'
                    },
                    {
                        id: 'ch.bafu.ren-fliessgewaesser_seen',
                        show: true,
                        opacity: 1,
                        name: 'map.layerLakes',
                        title: 'title.showLakes'
                    }
                ],

            }
        },

        computed: {
            netLayerURL() {
                return this.$store.getters.isFungusApp ? mapServiceUrls.mapLayerFungus : mapServiceUrls.mapLayerLichen
            },

            mapReady() {
                return this.$store.getters.mapIsReady
            },

            taxonId() {
                return this.$store.state.taxonId
            },

            timeRange() {
                return [this.$store.state.yearRange.min, this.$store.state.yearRange.max]
            },

            ecotypes() {
                return this.$store.state.ecotypes
            },

            queryEcotypeExpression() {
                if (['E', 'T', 'L', 'S'].every(el => this.ecotypes.includes(el))) {
                    return this.ecotypes.join("', '") + "', 'U', 'M', 'D"
                } else {
                    return this.ecotypes.join("', '")
                }
            },

            lang() {
                return i18n.locale
            },

        },

        watch: {
            taxonId() {
                this.loadDynamicLayers()
                this.updateObservationLayer()
            },
            timeRange() {
                this.loadDynamicLayers()
                this.updateObservationLayer()
            },
            ecotypes() {
                this.loadDynamicLayers()
                this.updateObservationLayer()
            },
            fullScreenActivated(value) {
                let div = document.querySelector('.map-template-container')

                // separate checks for cross-browser compatibility
                if (value) {
                    if (div.mozRequestFullScreen) {
                        div.mozRequestFullScreen()
                    } else if (div.webkitRequestFullscreen) {
                        div.webkitRequestFullscreen()
                    } else if (div.msRequestFullscreen) {
                        div.msRequestFullscreen()
                    }
                } else {
                    if (document.exitFullscreen) {
                        document.exitFullscreen()
                    } else if (document.webkitExitFullscreen) {
                        document.webkitExitFullscreen()
                    } else if (document.msExitFullscreen) {
                        document.msExitFullscreen()
                    }
                }
            },

            lang() {
                // reload headers if another language is selected
                this.headers = this.getHeaders()
            },


        },

        methods: {

            toggleFullscreen() {
                this.fullScreenActivated = !this.fullScreenActivated
            },


            toggleAdditionalLayer(layer) {
                if (layer.show) {
                    layer.show = false
                    layer.tileLayer.setVisible(false)

                } else {
                    layer.show = true
                    layer.tileLayer.setVisible(true)
                }

            },

            handleAdditionalLayer(layer) {
                if (layer.show) {
                    this.map.addLayer(layer.tileLayer)
                } else {
                    this.map.removeLayer(layer.tileLayer)
                }
            },

            scaleControl() {
                // see: https://openlayers.org/en/latest/examples/scale-line.html
                return new ScaleLine({
                    units: 'metric',
                    bar: true,
                    steps: 4,
                    text: true,
                    minWidth: 100
                })
            },


            WMTSTileLayerFactory(layer, layerOpacity, visibility, layerConfig, projection) {

                let matrixIds = []
                layerConfig.resolutions.forEach((item, i) => matrixIds.push(i))

                let tileGrid = new WMTSTileGrid({
                    origin: [layerConfig.extent[0], layerConfig.extent[3]],
                    resolutions: layerConfig.resolutions,
                    matrixIds: matrixIds
                })

                return new TileLayer({
                    source: new WMTS(({
                        crossOrigin: 'anonymous',   // avoid CORS error
                        url: 'https://wmts.geo.admin.ch/1.0.0/' + layer + '/default/current/2056/{TileMatrix}/{TileCol}/{TileRow}.jpeg',
                        tileGrid: tileGrid,
                        projection: projection,
                        layer: layer,
                        requestEncoding: 'REST'
                    })),
                    opacity: layerOpacity,
                    visible: visibility
                })

            },


            TileLayerFactory(layer, layerOpacity, visibility) {
                return new TileLayer({
                    source: new TileWMS({
                        attributions:
                            '© <a href="http://www.geo.admin.ch/internet/geoportal/' +
                            'en/home.html">Pixelmap 1:1000000 / geo.admin.ch</a>',
                        crossOrigin: 'anonymous',   // avoid CORS error
                        params: {
                            LAYERS: layer,
                            FORMAT: 'image/png'
                        },
                        url: 'https://wms.geo.admin.ch/' //https://wmts.geo.admin.ch/
                    }),
                    opacity: layerOpacity,
                    visible: visibility
                })

            },

            RendererFactory(color, outlineColor) {
                return {
                    type: 'simple',
                    symbol: {
                        type: 'esriSFS',
                        style: 'esriSFSSolid',
                        color: color,
                        outline: {
                            type: 'esriSLS',
                            style: 'esriSLSSolid',
                            color: outlineColor,
                            width: 0                // removed outline for lighter boxes
                        }

                    }
                }
            },

            DynamicLayerFactory(renderer, definitionExpression) {
                return {
                    id: 1,
                    source: {
                        type: 'mapLayer',
                        mapLayerId: 0
                    },
                    definitionExpression: definitionExpression,
                    drawingInfo: {
                        renderer: renderer,
                        transparency: 20
                    }
                }
            },


            loadDynamicLayers() {
                const InsideTimeRangeRenderer = this.RendererFactory([151, 14, 54], [0, 0, 0])
                const OutsideTimeRangeRenderer = this.RendererFactory([255, 143, 143], [0, 0, 0]) // lachs

                // alternative colours
                // const OutsideTimeRangeRenderer = this.RendererFactory([204, 90, 90], [0, 0, 0]) // dunkelorange
                // const OutsideTimeRangeRenderer = this.RendererFactory([245, 108, 108], [0, 0, 0]) // dunkellachs !!
                // const OutsideTimeRangeRenderer = this.RendererFactory([240, 120, 155], [0, 0, 0]) // kräftiger pink
                // const OutsideTimeRangeRenderer = this.RendererFactory([227, 61, 110], [0, 0, 0]) // sehr hell pink
                // const OutsideTimeRangeRenderer = this.RendererFactory([251, 142, 0], [0, 0, 0]) //orange

                if (this.$store.getters.isFungusApp) {
                    this.dynamicLayerInsideTimeRange = this.DynamicLayerFactory(InsideTimeRangeRenderer, 'artid= ' + this.taxonId + ' and cast(extract( year from lastdate) as int) >= ' + this.$store.state.yearRange.min + ' and cast(extract( year from lastdate) as int) <= ' + this.$store.state.yearRange.max)
                    this.dynamicLayerOutsideTimeRange = this.DynamicLayerFactory(OutsideTimeRangeRenderer, 'artid= ' + this.taxonId + ' and (cast(extract( year from lastdate) as int) < ' + this.$store.state.yearRange.min + ' or cast(extract( year from lastdate) as int) > ' + this.$store.state.yearRange.max + ')')

                } else if (this.$store.getters.isLichenApp) {

                    if(!this.queryEcotypeExpression.includes('XXX')){ // only render 5x5 km boxes if the ecotype (E, T, S, L) has been selected by the user or is unknown (U), muscicol (M), diverse (D)
                      this.dynamicLayerInsideTimeRange = this.DynamicLayerFactory(InsideTimeRangeRenderer, 'artid= ' + this.taxonId + " and (ecotyp in ('" + this.queryEcotypeExpression + "') )" + ' and cast(extract( year from lastdate) as int) >= ' + this.$store.state.yearRange.min + ' and cast(extract( year from lastdate) as int) <= ' + this.$store.state.yearRange.max)
                      this.dynamicLayerOutsideTimeRange = this.DynamicLayerFactory(OutsideTimeRangeRenderer, 'artid= ' + this.taxonId + " and (ecotyp in ('" + this.queryEcotypeExpression + "') )" + ' and (cast(extract( year from lastdate) as int) < ' + this.$store.state.yearRange.min + ' or cast(extract( year from lastdate) as int) > ' + this.$store.state.yearRange.max + ')')
                    } else { // also render observations for which the ecotype is null
                      this.dynamicLayerInsideTimeRange = this.DynamicLayerFactory(InsideTimeRangeRenderer, 'artid= ' + this.taxonId + " and (ecotyp in ('" + this.queryEcotypeExpression + "') or ecotyp is null)" + ' and cast(extract( year from lastdate) as int) >= ' + this.$store.state.yearRange.min + ' and cast(extract( year from lastdate) as int) <= ' + this.$store.state.yearRange.max)
                      this.dynamicLayerOutsideTimeRange = this.DynamicLayerFactory(OutsideTimeRangeRenderer, 'artid= ' + this.taxonId + " and (ecotyp in ('" + this.queryEcotypeExpression + "') or ecotyp is null)" + ' and (cast(extract( year from lastdate) as int) < ' + this.$store.state.yearRange.min + ' or cast(extract( year from lastdate) as int) > ' + this.$store.state.yearRange.max + ')')
                    }

                }


                this.dynamicLayers = [
                    this.dynamicLayerInsideTimeRange,
                    this.dynamicLayerOutsideTimeRange
                ]
            },


            ImageLayerFactory() {
                return new ImageLayer({
                    source: new ImageArcGISRest({
                        ratio: 1,
                        params: {
                            dynamicLayers: JSON.stringify(this.dynamicLayers)
                        },
                        url: this.netLayerURL,
                        crossOrigin: 'anonymous'    // avoid CORS error
                    }),
                    zIndex: 1 // keep 5x5 boxes on top of other map layers!
                })
            },

            countRenderedBoxes() {
                // alternatively: include the following line to only get counts within selected timespan:
                // +and+cast%28extract%28year+from+lastdate%29+as+int%29+%3E%3D+"+this.$store.state.yearRange.min+"+and+cast%28extract%28year+from+lastdate%29+as+int%29+%3C%3D+"+this.$store.state.yearRange.max+
                let urlInside = this.netLayerURL + '0/query?where=artid+%3D+' + this.taxonId + ' and cast(extract( year from lastdate) as int) >= ' + this.$store.state.yearRange.min + ' and cast(extract( year from lastdate) as int) <= ' + this.$store.state.yearRange.max + '&text=&objectIds=&time=&geometry=&geometryType=esriGeometryEnvelope&inSR=&spatialRel=esriSpatialRelIntersects&relationParam=&outFields=&returnGeometry=false&returnTrueCurves=false&maxAllowableOffset=&geometryPrecision=&outSR=&having=&returnIdsOnly=false&returnCountOnly=true&orderByFields=&groupByFieldsForStatistics=&outStatistics=&returnZ=false&returnM=false&gdbVersion=&historicMoment=&returnDistinctValues=false&resultOffset=&resultRecordCount=&queryByDistance=&returnExtentOnly=false&datumTransformation=&parameterValues=&rangeValues=&quantizationParameters=&f=pjson'
                let urlOutside = this.netLayerURL + '0/query?where=artid+%3D+' + this.taxonId + ' and (cast(extract( year from lastdate) as int) < ' + this.$store.state.yearRange.min + ' or cast(extract( year from lastdate) as int) > ' + this.$store.state.yearRange.max + ')' + '&text=&objectIds=&time=&geometry=&geometryType=esriGeometryEnvelope&inSR=&spatialRel=esriSpatialRelIntersects&relationParam=&outFields=&returnGeometry=false&returnTrueCurves=false&maxAllowableOffset=&geometryPrecision=&outSR=&having=&returnIdsOnly=false&returnCountOnly=true&orderByFields=&groupByFieldsForStatistics=&outStatistics=&returnZ=false&returnM=false&gdbVersion=&historicMoment=&returnDistinctValues=false&resultOffset=&resultRecordCount=&queryByDistance=&returnExtentOnly=false&datumTransformation=&parameterValues=&rangeValues=&quantizationParameters=&f=pjson'

                let totalBoxes = 0
                const getUrlInside = axios.get(urlInside)
                const getUrlOutside = axios.get(urlOutside)
                axios.all([getUrlInside, getUrlOutside]).then(axios.spread((...responses) => {
                    const responseUrlInside = responses[0]
                    const responseUrlOutside = responses[1]

                    totalBoxes += responseUrlInside.data.count
                    this.$store.commit('setTotal5x5boxesSelected', totalBoxes)

                    totalBoxes += responseUrlOutside.data.count
                    this.$store.commit('setTotal5x5boxes', totalBoxes)

                })).catch(errors => {
                    console.log(API_ERROR_MESSAGE, errors)
                    this.$store.commit('setTotal5x5boxesSelected', 0)
                    this.$store.commit('setTotal5x5boxes', 0)
                })



            },

            updateObservationLayer() {
                this.map.removeLayer(this.observationLayer)
                this.observationLayer = this.ImageLayerFactory()
                this.map.addLayer(this.observationLayer)

                this.countRenderedBoxes()
            },

            changeMousePositionProjection(event) {
                if (event === 'LV95/CH1903+') {
                    this.mousePositionControl.setProjection(this.projectionLV95)
                } else {
                    this.mousePositionControl.setProjection(this.projectionLV03)
                }
            },

            setMousePosition() {
                // cf: https://openlayers.org/en/latest/examples/mouse-position.html
                return new MousePosition({
                    coordinateFormat: createStringXY(1),
                    projection: this.projectionLV03,
                    className: 'custom-mouse-position',
                    target: document.getElementById('app-coordinates-display'),
                    undefinedHTML: '&nbsp;'
                })
            },

            setCoordinateProjections() {
                // note: the EPSG:2056 Swiss reference system needs to be explicitely defined and registered in OpenLayers
                // fix: https://gis.stackexchange.com/questions/286645/openlayers-ol-proj-get-returns-null-after-adding-projection-definition-to-proj4
                // examples (Swisstopo): https://codepen.io/geoadmin/pen/GZKEam , https://www.procrastinatio.org/2015/02/21/lv95-ol3/
                proj4.defs('EPSG:2056', '+proj=somerc +lat_0=46.95240555555556 +lon_0=7.439583333333333 +k_0=1 +x_0=2600000 +y_0=1200000 +ellps=bessel +towgs84=674.374,15.056,405.346,0,0,0,0 +units=m +no_defs')
                proj4.defs('EPSG:21781', '+proj=somerc +lat_0=46.95240555555556 +lon_0=7.439583333333333 +k_0=1 +x_0=600000 +y_0=200000 +ellps=bessel +towgs84=660.077,13.551,369.344,2.484,1.783,2.939,5.66 +units=m +no_defs')
                register(proj4)
                this.projectionLV95 = get('EPSG:2056')
                this.projectionLV03 = get('EPSG:21781')
                this.projectionLV95.setExtent(this.topoMap.extent)
            },


            getHeaders() {
                if (this.$store.getters.isFungusApp) {
                    return [
                        {text: i18n.t('observation.id'), align: 'left', value: 'id'},
                        {text: i18n.t('observation.variety'), align: 'left', value: 'variety'},
                        {text: i18n.t('observation.date'), align: 'left', value: 'date'},
                        {text: i18n.t('observation.location'), align: 'left', value: 'location'},
                        {text: i18n.t('observation.finder'), align: 'left', value: 'finder'},
                        {text: i18n.t('observation.herbarium'), align: 'left', value: 'herbarium'},
                        {text: i18n.t('observation.publication'), align: 'left', value: 'publication'}
                    ]
                } else {
                    return [
                        {text: i18n.t('observation.id'), align: 'left', value: 'id'},
                        {text: i18n.t('observation.date'), align: 'left', value: 'date'},
                        {text: i18n.t('observation.location'), align: 'left', value: 'location'},
                        {text: i18n.t('observation.substrate'), align: 'left', value: 'substrate'},
                        {text: i18n.t('observation.finder'), align: 'left', value: 'finder'},
                        {text: i18n.t('observation.herbarium'), align: 'left', value: 'herbarium'},
                        {text: i18n.t('observation.publication'), align: 'left', value: 'publication'}
                    ]
                }
            },

            processObservationInfo(observationData) {

                if (observationData.length === 0) {
                    console.log(OBSERVATION_ERROR_MESSAGE)
                } else {

                    for (let i = 0; i < observationData.length; i++) {

                        // special case for lichens: substrate is displayed in popup
                        if (this.$store.getters.isLichenApp && observationData[i].substrateCoarse) {
                            // TODO: @Silvia -> complete lichen.substrate table (IT, FR) before using this.lang for dynamic translations with DB
                            axios.get(diagramUrl.diagramBySubstrate + observationData[i].substrateCoarse + '?lang=de').then((result) => {

                                this.observations.push({
                                    id: observationData[i].id,
                                    date: new Date(observationData[i].observation_date).getFullYear(),
                                    location: observationData[i].municipality + ' (' + observationData[i].canton + ')',
                                    finder: observationData[i].finder,
                                    publication: observationData[i].publication === null ? i18n.t('general.noData') : observationData[i].publication,
                                    herbarium: observationData[i].herbarium === null ? i18n.t('observation.popupNoSpecimen') : (observationData[i].herbarium === 'PRIV' ? i18n.t('general.priv') : observationData[i].herbarium ),
                                    substrate: observationData[i].substrateCoarse === null ? i18n.t('general.noData') : result.data[0].substrate,// substrate is displayed only for LICHEN
                                    variety: observationData[i].variety === null ? i18n.t('general.noData') : observationData[i].variety
                                })

                            }).catch((error) => {
                                console.log(API_ERROR_MESSAGE, error)
                                this.substrate = ''
                            })
                        } else {
                            this.observations.push({
                                id: observationData[i].id,
                                date: new Date(observationData[i].observation_date).getFullYear(),
                                location: observationData[i].municipality + ' (' + observationData[i].canton + ')',
                                finder: observationData[i].finder,
                                publication: observationData[i].publication === null ? i18n.t('general.noData') : observationData[i].publication,
                                herbarium: observationData[i].herbarium === null ? i18n.t('observation.popupNoSpecimen') : observationData[i].herbarium,
                                variety: observationData[i].variety === null ? i18n.t('general.noData') :observationData[i].variety

                            })
                        }

                    }
                    this.showPopupObservation = true

                }
            },

            resetPopup() {
                this.showPopupObservation = false
                this.observations = []
            },

            updatePopup() {
                this.resetPopup()

                let observationURL = speciesUrl.speciesObservation + 'taxonId=' + this.taxonId + '&x=' + this.X + '&y=' + this.Y
                axios.get(observationURL).then((result) => {
                    this.processObservationInfo(result.data)
                })
            }

        },

        mounted() {

            // initialize header with translations from i18n for currently selected language from store
            this.headers = this.getHeaders()

            // initialize projections
            this.setCoordinateProjections()

            // initialize available layers
            this.swissBaseLayer = this.WMTSTileLayerFactory('ch.swisstopo.pixelkarte-farbe', 0.8, true, this.topoMap, this.projectionLV95)

            // create tile layers for additional layers (biogeoreg, cantons, relief)
            this.layers.forEach((layer) => {
                layer.tileLayer = this.TileLayerFactory(layer.id, layer.opacity, layer.show)
            })

            // initialize mouse position with current projection
            this.mousePositionControl = this.setMousePosition()

            // initialize map with swiss topo layer and relief layer (incl. lakes)
            this.map = new Map({
                layers: [this.swissBaseLayer, this.layers[2].tileLayer, this.layers[3].tileLayer],
                target: 'map-container',
                controls: defaultControls().extend([this.mousePositionControl, this.scaleControl()]),
                interactions: defaults({        // add touch drag/zoom interactions for mobile devices
                    dragPan: false,
                    mouseWheelZoom: false
                }),
                view: new View({
                    center: [2665000, 1185000],
                    projection: this.projectionLV95,
                    resolution: 550,
                    maxZoom: 15,
                    extent: this.topoMap.extent,
                    map: this.map
                })
            })

            // GoogleMaps-like zoom/pan on mobile devices: https://codepen.io/HansB1984/pen/OJLogga
            // https://github.com/openlayers/openlayers/issues/9936#issuecomment-53207947
            // use normal behaviour in desktop browsers (strg+mousewheel) vs. 2-finger scroll movement in mobile devices
            this.map.addInteraction(new MouseWheelZoom({ condition: e => e.originalEvent.ctrlKey }))
            this.map.addInteraction(new DragPan({
                condition: function(e) {
                    return noModifierKeys(e) && (!/Mobi|Android/i.test(navigator.userAgent) || this.targetPointers.length === 2)
                }
            }))

            let lastTouchY = null
            const div = document.getElementById('map-container')
            const scrollerBlades = document.scrollingElement || document.documentElement
            div.addEventListener('touchmove', function (e) {
                e.preventDefault()
                const touches = e.touches || e.changedTouches
                if (touches.length === 1) { // 1-finger touch movement
                    if (lastTouchY !== null) {
                        const by = lastTouchY - touches[0].clientY
                        scrollerBlades.scrollTop += by
                    }
                    lastTouchY = touches[0].clientY
                }
            })

            // reset y position as soon as touch event ends
            div.addEventListener('touchend', e => { console.log(e); lastTouchY = null })



            // display fungus record info in popup window (TODO: hover over 5x5km boxes -> pointermove)
            this.map.on('click', (evt) => {
                if (this.taxonId !== null) {


                    // convert Swiss coordinates from LV95 into LV03 to retrieve corresponding data via rest api
                    let transformedCoordinates = transform(evt.coordinate, this.projectionLV95, this.projectionLV03)
                    this.X = transformedCoordinates[0]
                    this.Y = transformedCoordinates[1]

                    this.resetPopup()
                    this.updatePopup()
                    this.renderPopupObservation = true
                }
            })

            this.$store.commit('setReady', true)
        }

    }


</script>

<style scoped lang="scss">
    @import "../../../../global";

    .hide {
        display: none;
    }

    #mask-container {
        position: absolute;
        background: rgba(255, 51, 0, 0.1);
        border: 2px dashed rgb(255, 51, 0);
    }

    .map-template-container {
        position: relative;
        height: 600px;
        width: 100%;
        margin: auto;
        border: 2px solid $app-dark;
        border-radius: 5px;

    }

    #map-container {
        position: relative;
        height: 100%;
        width: 100%;
        border: 1px solid $app-black;
        border-radius: 5px;
    }

    #app-additional-layers-button {
        position: absolute;
        background-color: $app-black;
        right: 0;
    }


    #app-cursor-position {
        font-size: 12px;
        margin-top: 5px;
        margin-bottom: 30px;
        padding: 2px
    }


    #app-coordinates-display {
        margin-top: 3px;
        font-size: 12px;
        padding: 8px;
        width: fit-content;
    }

    :fullscreen {
        background-color: $app-dark;
        position: relative !important;
    }

    .active {
        color: $app-light-yellow;
    }

    .app-layer-button {
        background-color: $app-black !important;
        color: $app-light-yellow;

    }


</style>
